import React, { useState } from 'react';
import { ChevronDown, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { companyInfo } from '../data/mock';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "How do I get a quote for my move?",
      answer: "Call us on 01273 044565 or send us a WhatsApp message. We'll ask about your move details and give you a competitive price on the spot — no forms to fill in."
    },
    {
      question: "How far in advance should I book?",
      answer: "We recommend booking at least 2 weeks in advance, especially for weekend moves. However, we also offer same-day and urgent services if you need to move quickly. Contact us to check availability."
    },
    {
      question: "Are you insured?",
      answer: "Yes, we carry comprehensive goods in transit insurance and public liability insurance. Your belongings are protected throughout the move. We can provide certificates of insurance upon request."
    },
    {
      question: "What areas do you cover?",
      answer: "We're based in Brighton and cover all of Sussex and the South Coast. We also handle national moves throughout the UK. Contact us for moves outside these areas."
    },
    {
      question: "Do you provide packing materials?",
      answer: "Yes, we supply high-quality packing materials including boxes, bubble wrap, tape, and wardrobe boxes. We can also provide a full packing service if you'd prefer us to pack everything for you."
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept cash, bank transfer, and all major credit and debit cards. Payment is typically due upon completion of the move unless otherwise agreed."
    },
    {
      question: "Can you move a single item?",
      answer: "Absolutely! We're happy to move single items such as sofas, beds, or appliances. Our same-day service is perfect for quick single-item deliveries."
    },
    {
      question: "Do you offer storage services?",
      answer: "Yes, we have secure, climate-controlled storage facilities available for both short and long-term storage. This is perfect if there's a gap between your move-out and move-in dates."
    },
    {
      question: "What happens if something gets damaged?",
      answer: "While we take every precaution to prevent damage, accidents can happen. All claims should be reported within 7 days. Our insurance covers damage that occurs during the move, subject to policy terms."
    },
    {
      question: "Can you disassemble and reassemble furniture?",
      answer: "Yes, our team can disassemble beds, wardrobes, tables, and other furniture at the pickup location and reassemble them at your new home. Just let us know in advance."
    }
  ];

  // Generate FAQ Schema for SEO
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-24 bg-gray-50">
      {/* FAQ Schema for SEO */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      
      <div className="max-w-4xl mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-[#3B43AF] font-semibold text-sm uppercase tracking-wider mb-3">FAQ</span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
          <p className="text-xl text-gray-600">
            Got questions? We've got answers. If you can't find what you're looking for, give us a call.
          </p>
        </div>

        {/* FAQ List */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-5 text-left flex items-center justify-between gap-4 hover:bg-gray-50 transition-colors"
              >
                <span className="font-semibold text-gray-900 text-lg">{faq.question}</span>
                <ChevronDown 
                  className={`w-5 h-5 text-[#3B43AF] flex-shrink-0 transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`} 
                />
              </button>
              <div 
                className={`overflow-hidden transition-all duration-300 ease-in-out ${openIndex === index ? 'max-h-96' : 'max-h-0'}`}
              >
                <p className="px-6 pb-5 text-gray-600 leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">Still have questions?</p>
          <Button 
            asChild
            className="bg-[#3B43AF] hover:bg-[#2f3691] text-white px-8 py-6 text-lg rounded-xl"
          >
            <a href={`tel:${companyInfo.phone.replace(/\s/g, '')}`} className="flex items-center gap-2">
              <Phone className="w-5 h-5" />
              Call Us: {companyInfo.phone}
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
